import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  ArrowLeft, Brain, Lightbulb, Clock, User, Mail, Phone, MapPin,
  Send, MessageSquare, Monitor, AlertTriangle, CheckCircle, FileText,
  Loader2, Sparkles, ExternalLink, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TicketDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    fetchTicket();
  }, [id, token]);

  const fetchTicket = async () => {
    try {
      const response = await fetch(`${API}/api/support/tickets/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTicket(data);
        if (data.ai_summary) {
          setAiSummary(data.ai_summary);
        }
        if (data.notes) {
          setNotes(data.notes);
        }
      }
    } catch (error) {
      console.error('Fetch ticket error:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateAISummary = async () => {
    setLoadingAI(true);
    try {
      const response = await fetch(`${API}/api/support/tickets/${id}/ai-summary`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAiSummary(data);
        toast.success('AI summary generated');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to generate AI summary');
      }
    } catch (error) {
      toast.error('Error generating AI summary');
    } finally {
      setLoadingAI(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setSendingNote(true);
    try {
      const response = await fetch(`${API}/api/support/tickets/${id}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: newNote })
      });
      if (response.ok) {
        const data = await response.json();
        // Add the new note to the local state
        const newNoteObj = {
          id: Date.now(),
          author: 'You',
          content: newNote,
          created_at: new Date().toISOString()
        };
        setNotes(prevNotes => [newNoteObj, ...prevNotes]);
        toast.success('Note added successfully');
        setNewNote('');
      } else {
        toast.error('Failed to add note');
      }
    } catch (error) {
      toast.error('Error adding note');
    } finally {
      setSendingNote(false);
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      const response = await fetch(`${API}/api/support/tickets/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        setTicket(prev => ({ ...prev, status: newStatus }));
        toast.success(`Ticket marked as ${newStatus}`);
        if (newStatus === 'resolved' || newStatus === 'closed') {
          navigate('/tickets');
        }
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Error updating status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleEscalate = async () => {
    await handleUpdateStatus('escalated');
  };

  const handleResolve = async () => {
    await handleUpdateStatus('resolved');
  };

  const handleSendEmail = () => {
    if (displayTicket.customer_email) {
      window.location.href = `mailto:${displayTicket.customer_email}?subject=Re: ${displayTicket.subject}`;
    } else {
      toast.error('No customer email available');
    }
  };

  const handleRemoteSupport = () => {
    toast.info('Remote support feature - Opening remote session...');
    // In production, this would open a remote support tool
  };

  const handleStartWhatsApp = () => {
    if (displayTicket.customer_phone) {
      const phone = displayTicket.customer_phone.replace(/\D/g, '');
      window.open(`https://wa.me/${phone}`, '_blank');
    } else {
      toast.error('No customer phone number available');
    }
  };

  const handleCreateTask = () => {
    toast.success('Task created for this ticket');
    // In production, this would open a task creation modal
  };

  const handleViewKnowledgeArticle = (articleTitle) => {
    // Navigate to knowledge base with search
    navigate(`/knowledge-base?search=${encodeURIComponent(articleTitle)}`);
  };

  // Sample data for demo
  const sampleTicket = {
    id: id || 'TKT-1001',
    customer_name: 'John Smith',
    customer_email: 'john.smith@example.com',
    customer_phone: '+1 555-0123',
    customer_location: 'New York, USA',
    subject: 'Software installation issue - Product not activating',
    description: 'I recently purchased the software and after installation, the product is not activating. I have tried reinstalling multiple times but the issue persists. The error message shows "License validation failed". I need urgent assistance as this is affecting my work.',
    status: 'in-progress',
    priority: 'high',
    channel: 'whatsapp',
    category: 'Technical',
    sub_category: 'Activation',
    agent: 'Sarah M.',
    sla: '4 hours',
    due_date: '2025-05-21T18:00:00Z',
    created_at: '2025-05-21T08:30:00Z',
    updated_at: '2025-05-21T10:30:00Z',
    activities: [
      { action: 'Ticket created', actor: 'System', time: '2025-05-21T08:30:00Z' },
      { action: 'Assigned to Sarah M.', actor: 'Auto-assign', time: '2025-05-21T08:31:00Z' },
      { action: 'Status changed to In Progress', actor: 'Sarah M.', time: '2025-05-21T09:00:00Z' },
      { action: 'AI Summary generated', actor: 'AI System', time: '2025-05-21T09:15:00Z' },
    ]
  };

  const sampleNotes = [
    { id: 1, author: 'Sarah M.', content: 'Customer contacted, troubleshooting license issue', created_at: '2025-05-21T09:00:00Z' },
    { id: 2, author: 'AI Assistant', content: 'Suggested checking firewall settings and license server connectivity', created_at: '2025-05-21T09:15:00Z' },
  ];

  const sampleAISummary = {
    summary: 'Customer experiencing software activation failure after fresh installation. Multiple reinstallation attempts unsuccessful. Error indicates license validation issue.',
    root_cause: 'Likely causes include: network connectivity to license server, firewall blocking outbound connections, or corrupted license file.',
    suggested_actions: [
      'Verify internet connectivity and firewall settings',
      'Check if license server URL is accessible',
      'Clear license cache and retry activation',
      'Regenerate license key from admin portal'
    ],
    recommended_solutions: [
      { title: 'License Activation Troubleshooting', type: 'KB Article', link: '/knowledge-base?search=license+activation' },
      { title: 'Firewall Configuration Guide', type: 'Documentation', link: '/knowledge-base?search=firewall+configuration' },
      { title: 'License Reset Procedure', type: 'Internal', link: '/knowledge-base?search=license+reset' }
    ],
    confidence: 87
  };

  const displayTicket = ticket || sampleTicket;
  const displayAISummary = aiSummary || sampleAISummary;
  const displayNotes = notes.length > 0 ? notes : sampleNotes;

  const getStatusClass = (status) => {
    const classes = {
      'open': 'status-open',
      'in-progress': 'status-in-progress',
      'resolved': 'status-resolved',
      'closed': 'status-closed',
      'escalated': 'status-escalated'
    };
    return classes[status] || 'status-open';
  };

  const getPriorityClass = (priority) => {
    const classes = {
      'high': 'priority-high',
      'medium': 'priority-medium',
      'low': 'priority-low'
    };
    return classes[priority] || 'priority-medium';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <Link to="/tickets" className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Tickets
          </Link>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{displayTicket.id}</h1>
            <span className={`status-badge ${getStatusClass(displayTicket.status)}`}>
              {displayTicket.status?.replace('-', ' ')}
            </span>
          </div>
          <p className="text-muted-foreground mt-1">{displayTicket.subject}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button 
            onClick={handleEscalate}
            disabled={updatingStatus || displayTicket.status === 'escalated'}
            className="support-btn-secondary flex items-center gap-2"
            data-testid="escalate-btn"
          >
            <AlertTriangle className="w-4 h-4" />
            Escalate
          </button>
          <button 
            onClick={handleSendEmail}
            className="support-btn-secondary flex items-center gap-2"
            data-testid="send-email-btn"
          >
            <Mail className="w-4 h-4" />
            Send Email
          </button>
          <button 
            onClick={handleRemoteSupport}
            className="support-btn-secondary flex items-center gap-2"
            data-testid="remote-support-btn"
          >
            <Monitor className="w-4 h-4" />
            Remote Support
          </button>
          <button 
            onClick={handleResolve}
            disabled={updatingStatus || displayTicket.status === 'resolved'}
            className="support-btn-primary flex items-center gap-2"
            data-testid="resolve-btn"
          >
            {updatingStatus ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            Resolve
          </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Tabs */}
          <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto">
            {['overview', 'notes', 'conversation', 'activity'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                  activeTab === tab ? 'bg-primary text-primary-foreground' : 'hover:bg-secondary'
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Description */}
              <div className="support-card">
                <h3 className="font-semibold mb-3">Issue Description</h3>
                <p className="text-muted-foreground">{displayTicket.description}</p>
              </div>

              {/* AI Summary */}
              <div className="support-card border-primary/30">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Brain className="w-5 h-5 text-primary" />
                    <h3 className="font-semibold">AI Summary</h3>
                  </div>
                  <button
                    onClick={generateAISummary}
                    disabled={loadingAI}
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    {loadingAI ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    {loadingAI ? 'Generating...' : 'Regenerate'}
                  </button>
                </div>
                <p className="text-muted-foreground mb-4">{displayAISummary.summary}</p>
                
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4">
                  <p className="text-sm font-medium text-amber-400 mb-1">Probable Root Cause</p>
                  <p className="text-sm text-muted-foreground">{displayAISummary.root_cause}</p>
                </div>

                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>AI Confidence:</span>
                  <div className="flex-1 h-2 bg-secondary rounded-full max-w-32">
                    <div 
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${displayAISummary.confidence}%` }}
                    ></div>
                  </div>
                  <span className="text-primary font-medium">{displayAISummary.confidence}%</span>
                </div>
              </div>

              {/* AI Suggested Actions */}
              <div className="support-card">
                <div className="flex items-center gap-2 mb-4">
                  <Lightbulb className="w-5 h-5 text-amber-400" />
                  <h3 className="font-semibold">AI Suggested Actions</h3>
                </div>
                <div className="space-y-2">
                  {displayAISummary.suggested_actions.map((action, index) => (
                    <div 
                      key={index} 
                      onClick={() => toast.success(`Action "${action}" marked as done`)}
                      className="flex items-start gap-3 p-3 bg-secondary/50 rounded-lg cursor-pointer hover:bg-secondary transition-colors"
                    >
                      <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-sm font-medium shrink-0">
                        {index + 1}
                      </div>
                      <p className="text-sm">{action}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommended Solutions - Now clickable and navigates to KB */}
              <div className="support-card">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-blue-400" />
                  <h3 className="font-semibold">Recommended Solutions</h3>
                </div>
                <div className="space-y-2">
                  {displayAISummary.recommended_solutions.map((solution, index) => (
                    <div 
                      key={index}
                      onClick={() => handleViewKnowledgeArticle(solution.title)}
                      className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg hover:bg-secondary transition-colors cursor-pointer"
                      data-testid={`solution-${index}`}
                    >
                      <div>
                        <p className="text-sm font-medium">{solution.title}</p>
                        <p className="text-xs text-muted-foreground">{solution.type}</p>
                      </div>
                      <ExternalLink className="w-4 h-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Add Note */}
              <div className="support-card">
                <h3 className="font-semibold mb-3">Add Internal Note</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                    placeholder="Type your note..."
                    className="support-input flex-1"
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={sendingNote || !newNote.trim()}
                    className="support-btn-primary px-4"
                  >
                    {sendingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Notes Tab - Shows all notes */}
          {activeTab === 'notes' && (
            <div className="space-y-4">
              {/* Add Note */}
              <div className="support-card">
                <h3 className="font-semibold mb-3">Add New Note</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddNote()}
                    placeholder="Type your note..."
                    className="support-input flex-1"
                  />
                  <button
                    onClick={handleAddNote}
                    disabled={sendingNote || !newNote.trim()}
                    className="support-btn-primary px-4"
                  >
                    {sendingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Notes List */}
              <div className="support-card">
                <h3 className="font-semibold mb-4">All Notes ({displayNotes.length})</h3>
                {displayNotes.length > 0 ? (
                  <div className="space-y-4">
                    {displayNotes.map((note, index) => (
                      <div key={note.id || index} className="p-4 bg-secondary/50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">{note.author}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(note.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{note.content}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No notes yet</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="support-card">
              <h3 className="font-semibold mb-4">Activity Timeline</h3>
              <div className="space-y-4">
                {displayTicket.activities?.map((activity, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2"></div>
                    <div>
                      <p className="text-sm">{activity.action}</p>
                      <p className="text-xs text-muted-foreground">
                        {activity.actor} • {new Date(activity.time).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'conversation' && (
            <div className="support-card text-center py-12">
              <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No conversation history yet</p>
              <button 
                onClick={handleStartWhatsApp}
                className="support-btn-primary mt-4"
              >
                Start WhatsApp Chat
              </button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Customer Info */}
          <div className="support-card">
            <h3 className="font-semibold mb-4">Customer Information</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                  {displayTicket.customer_name?.charAt(0)}
                </div>
                <div>
                  <p className="font-medium">{displayTicket.customer_name}</p>
                  <p className="text-sm text-muted-foreground">Customer</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span>{displayTicket.customer_email}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Phone className="w-4 h-4 text-muted-foreground" />
                <span>{displayTicket.customer_phone}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-muted-foreground" />
                <span>{displayTicket.customer_location}</span>
              </div>
              <button className="text-primary text-sm hover:underline">View Customer Profile</button>
            </div>
          </div>

          {/* Ticket Properties */}
          <div className="support-card">
            <h3 className="font-semibold mb-4">Ticket Properties</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Priority</span>
                <span className={`status-badge ${getPriorityClass(displayTicket.priority)}`}>
                  {displayTicket.priority}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Category</span>
                <span>{displayTicket.category}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Sub Category</span>
                <span>{displayTicket.sub_category || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Channel</span>
                <span className="capitalize">{displayTicket.channel}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Agent</span>
                <span>{displayTicket.agent}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">SLA</span>
                <span>{displayTicket.sla}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Due Date</span>
                <span>{new Date(displayTicket.due_date).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Quick Actions - Now functional */}
          <div className="support-card">
            <h3 className="font-semibold mb-4">Quick Actions</h3>
            <div className="space-y-2">
              <button 
                onClick={handleStartWhatsApp}
                className="w-full support-btn-secondary text-left flex items-center gap-2"
                data-testid="quick-whatsapp"
              >
                <MessageSquare className="w-4 h-4" />
                Start WhatsApp Chat
              </button>
              <button 
                onClick={handleRemoteSupport}
                className="w-full support-btn-secondary text-left flex items-center gap-2"
                data-testid="quick-remote"
              >
                <Monitor className="w-4 h-4" />
                Remote Support
              </button>
              <button 
                onClick={handleCreateTask}
                className="w-full support-btn-secondary text-left flex items-center gap-2"
                data-testid="quick-task"
              >
                <FileText className="w-4 h-4" />
                Create Task
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
